import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Menu,
  ListItemIcon,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Add as AddIcon } from "@mui/icons-material";
import { CMSContext } from "../context/CaseManagmentContext";
import moment from "moment";
import { toast } from "react-toastify";
import ConfirmationDialog from "../../../dialogs/ConfirmationDialog";
import TaskFormDialog from "../../../dialogs/TaskFormDialog";
import { showInsufficientCreditsModal } from "../../../actions/creditActions";
import { deductCredits } from "../../../utils/HelperFunction/deductCredits";
import { useDispatch, useSelector } from "react-redux";
import { Delete } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import {
  getCaseTasksApi,
  getCaseTaskByIdApi,
  createCaseTaskApi,
  updateCaseTaskApi,
  deleteCaseTaskApi,
} from "../../../services/api/caseManagementApi";
export default function TasksTable() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const { selectedCase, advocates, fetchCases, taskRefreshKey } = useContext(CMSContext);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);
  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState({});

  const caseAdvocate = selectedCase?.advocates?.find(
    (advocate) => advocate.userId === userId,
  );

  const isOwner =
    selectedCase?.createdBy === userId || caseAdvocate?.role === "Owner";

  const [anchorEl, setAnchorEl] = useState(null);
  const fetchTasksInFlightRef = useRef(false);

  const handleOpen = (task) => {
    setEditingTask(task || null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingTask(null);
  };

  const handleSave = async (formData) => {
    if (!formData || !selectedCase) {
      toast.error("Missing required data");
      return;
    }

    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }

    try {
      setLoading(true);
      if (!editingTask) {
        formData.startDate = moment().format("YYYY-MM-DD");
      }
      const response = editingTask
        ? await updateCaseTaskApi(selectedCase._id, formData._id, formData)
        : await createCaseTaskApi(selectedCase._id, formData);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(`Failed to ${editingTask ? "update" : "create"} task`);
      }

      const data = response.data;
      fetchTasks();
      if (editingTask) {
        setTasks((prevTasks) =>
          prevTasks.map((task) => (task._id === editingTask._id ? data : task)),
        );
      } else {
        setTasks((prevTasks) => [...prevTasks, data]);
      }

      handleClose();

      const deductionSuccess = deductCredits(
        { userId, creditsToDeduct: 1, event: "Task added/edit on case management page." },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn("Failed to deduct credits. Ensure retry or user notification.");
      }
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Failed to save task. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignedToChange = async (task, newValue) => {
    if (!newValue) return;
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }
    const updatedTask = { ...task, assignedTo: newValue._id };

    try {
      setLoading(true);
      const response = await updateCaseTaskApi(selectedCase._id, task._id, updatedTask);

      if (response.status !== 200) {
        throw new Error("Failed to update task");
      }

      setTasks((prevTasks) =>
        prevTasks.map((t) => (t._id === task._id ? updatedTask : t)),
      );
      toast.success("Task updated successfully");
      // Deduct credits
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Task assigned to changed on case management page.",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }

    const updatedTask = { ...task, status: newStatus };

    try {
      setLoading(true);
      const response = await updateCaseTaskApi(selectedCase._id, task._id, updatedTask);

      if (response.status !== 200) {
        throw new Error("Failed to update task status");
      }
      setTasks((prevTasks) =>
        prevTasks.map((t) => (t._id === task._id ? updatedTask : t)),
      );
      toast.success("Task status updated successfully");
      // Deduct credits
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Task status to changed on case management page.",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      toast.error("Failed to update task status");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id || !selectedCase) {
      // console.error("Task ID or Case ID is missing");
      return;
    }

    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }

    try {
      setLoading(true);

      const response = await deleteCaseTaskApi(selectedCase._id, id);

      if (response.status !== 200) {
        toast.error("Failed to delete the task");
        throw new Error("Failed to delete the task");
      }

      // Optimistically update the UI
      setTasks((prevTasks) => prevTasks.filter((task) => task._id !== id));
      toast.success("Task deleted successfully.");
      setDeleteModal(false);
      setTaskToDelete(null);
      // Deduct credits
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Task delete on case management page.",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error deleting task:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCase?._id) fetchTasks();
  }, [selectedCase?._id, taskRefreshKey]);

  const fetchTasks = async () => {
    if (!selectedCase?._id || fetchTasksInFlightRef.current) return;

    fetchTasksInFlightRef.current = true;

    try {
      setLoading(true);
      setTasks([]);
      const response = await getCaseTasksApi(selectedCase._id);
      if (response.status !== 200) throw new Error("Failed to fetch tasks");
      const data = response.data;
      setTasks(
        data?.tasks?.map((task) => ({
          ...task,
          startDate: moment(task.startDate).format("YYYY-MM-DD"),
          dueDate: moment(task.dueDate).format("YYYY-MM-DD"),
        })),
      );
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
      fetchTasksInFlightRef.current = false;
    }
  };

  const fetchTaskById_unused = async (taskId) => {
    // Kept for reference only — not called anywhere
    try {
      setLoading(true);
      const response = await getCaseTaskByIdApi(selectedCase._id, taskId);
      if (response.status !== 200) throw new Error("Failed to fetch task by ID");
    } catch (error) {
      console.error("Error fetching task by ID:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          sx={{ fontSize: "18px", fontWeight: "bold", color: "#003D78" }}
        >
          Tasks
        </Typography>
        {isOwner && (
          <Button
            size="small"
            variant="contained"
            sx={{
              backgroundColor: "#003D78",
              color: "#FFFFFF",
              textTransform: "capitalize",
            }}
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Add Task
          </Button>
        )}
      </Box>

      <TableContainer
        sx={{ borderTopLeftRadius: "10px", borderTopRightRadius: "10px" }}
        component={Paper}
      >
        <Table>
          <TableHead sx={{ backgroundColor: "#003D78", color: "#FFFFFF" }}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "#FFFFFF",
                  py: "6px",
                }}
              >
                Task Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "#FFFFFF",
                  py: "6px",
                }}
              >
                Task Description
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "#FFFFFF",
                  py: "6px",
                }}
              >
                Assigned To
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "#FFFFFF",
                  py: "6px",
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "#FFFFFF",
                  py: "6px",
                }}
              >
                Start Date
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "#FFFFFF",
                  py: "6px",
                }}
              >
                Due Date
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "#FFFFFF",
                  py: "6px",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={12}
                  sx={{
                    textAlign: "center",
                    padding: "16px",
                    fontSize: "14px",
                    color: "#777",
                  }}
                >
                  <CircularProgress size={18} sx={{ color: "#003D78" }} />
                </TableCell>
              </TableRow>
            ) : tasks && tasks.length > 0 ? (
              tasks?.map((task) => (
                <TableRow key={task?._id}>
                  <TableCell
                    sx={{
                      maxWidth: 60, // Adjust as needed
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      py: 1,
                      fontSize: "13px",
                    }}
                  >
                    {task?.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 110, // Adjust as needed
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      py: 1,
                      fontSize: "13px",
                    }}
                  >
                    {task?.description}
                  </TableCell>
                  <TableCell sx={{ py: 1, fontSize: "13px" }}>
                    {selectedCase?.lawyerDetails.find(
                      (advocate) => advocate.userId === task.assignedTo,
                    )?.name || null}
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        sx={{ fontSize: "13px", maxWidth: "10rem" }}
                        value={task.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          setTasks(
                            tasks.map((t) =>
                              t._id === task._id
                                ? { ...t, status: newStatus }
                                : t,
                            ),
                          );
                          await handleStatusChange(task, newStatus);
                        }}
                      >
                        <MenuItem sx={{ fontSize: "13px" }} value="Pending">
                          Pending
                        </MenuItem>
                        <MenuItem sx={{ fontSize: "13px" }} value="InProcess">
                          In Progress
                        </MenuItem>
                        <MenuItem sx={{ fontSize: "13px" }} value="Completed">
                          Completed
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1, fontSize: "13px" }}>
                    {moment(task.startDate).format("ll")}
                  </TableCell>
                  <TableCell sx={{ py: 1, fontSize: "13px" }}>
                    {moment(task.dueDate).format("ll")}
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        sx={{
                          color: "inherit",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                        onClick={(event) => {
                          setSelectedItem(task);
                          setAnchorEl(event.currentTarget);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        PaperProps={{
                          sx: {
                            //mt: 3.5,
                            boxShadow:
                              "0px 4px 12px rgba(0, 0, 0, 0.1) !important",
                            borderRadius: "8px",
                          },
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            handleOpen(selectedItem);
                            setAnchorEl(null);
                          }}
                        >
                          <ListItemIcon>
                            <FontAwesomeIcon
                              size="xs"
                              icon={faPenToSquare}
                              style={{ color: "#2196f3" }}
                            />
                          </ListItemIcon>
                          <Typography sx={{ fontSize: "13px" }}>
                            {isOwner ? "Edit" : "Preview"}
                          </Typography>
                        </MenuItem>

                        {isOwner && (
                          <MenuItem
                            onClick={() => {
                              setTaskToDelete(selectedItem);
                              setDeleteModal(true);
                              setAnchorEl(null);
                            }}
                          >
                            <ListItemIcon size="xs">
                              <Delete
                                fontSize="small"
                                sx={{ color: "#d2042d", fontSize: "16px" }}
                              />
                            </ListItemIcon>
                            <Typography sx={{ fontSize: "13px" }}>
                              Delete
                            </Typography>
                          </MenuItem>
                        )}
                      </Menu>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={12}
                  sx={{
                    textAlign: "center",
                    padding: "16px",
                    fontSize: "14px",
                    color: "#777",
                  }}
                >
                  No tasks found for this case.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TaskFormDialog
        open={open}
        onClose={handleClose}
        onSave={handleSave}
        editingTask={editingTask}
        isOwner={isOwner}
        advocates={advocates}
        selectedCase={selectedCase}
        loading={loading}
      />
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteModal}
        onClose={() => {
          setTaskToDelete(null);
          setDeleteModal(false);
        }}
        onConfirm={() => {
          if (deleteModal) {
            handleDelete(taskToDelete?._id);
          }
        }}
        title="Confirm Delete"
        contentText={`Are you sure you want to delete the task "${taskToDelete?.name}"? This action cannot be undone.`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        loading={loading}
      />
    </Box>
  );
}
